import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { PentacodeView } from "../../integration/pentacode-view";
import { shared } from "../../styles";

if (!customElements.get("pentacode-view")) {
    customElements.define("pentacode-view", PentacodeView);
}

const VIEWS = [
    { title: "Arbeitszeiten", path: "employees/all/time" },
    { title: "Urlaubskonto", path: "employees/all/vacation-balance" },
    { title: "Arbeitszeitkonto", path: "employees/all/time-balance" },
    { title: "Vorschüsse", path: "employees/all/advances" },
];

function monthRange(month: string) {
    const [year, m] = month.split("-").map(Number);
    const next = m === 12 ? `${year + 1}-01` : `${year}-${String(m + 1).padStart(2, "0")}`;
    return { from: `${month}-01`, to: `${next}-01` };
}

/**
 * Demo of `<pentacode-view>` inside the app itself: four pages as
 * standalone views, each with its own router, sharing the session and the
 * query cache. This page plays the host: it owns the month, the views follow
 * it, and a range picked inside a view flows back.
 */
@customElement("ptc-views-demo")
export class ViewsDemo extends LitElement {
    @state()
    private _month = new Date().toISOString().slice(0, 7);

    static styles = [
        shared,
        css`
            :host {
                display: flex;
                flex-direction: column;
                gap: 1em;
                padding: 1em;
                overflow: auto;
            }

            .grid {
                flex: 1;
                display: grid;
                grid-template-columns: 1fr 1fr;
                grid-template-rows: 1fr 1fr;
                gap: 1em;
                min-height: 40em;
            }

            .panel {
                display: flex;
                flex-direction: column;
                border: solid 1px var(--shade-2);
                border-radius: 0.5em;
                overflow: hidden;
            }

            .panel > h3 {
                margin: 0;
                padding: 0.5em 0.75em;
                border-bottom: solid 1px var(--shade-2);
            }

            pentacode-view {
                flex: 1;
            }
        `,
    ];

    private _onRouteChange(path: string, e: CustomEvent<{ route: string }>) {
        const [routePath, query = ""] = e.detail.route.split("?");
        const from = new URLSearchParams(query).get("from");
        if (routePath === path && from) {
            this._month = from.slice(0, 7);
        }
    }

    render() {
        const { from, to } = monthRange(this._month);
        return html`
            <div class="horizontal center-aligning spacing layout">
                <h2 class="stretch">Ansichten (Demo)</h2>
                <input
                    type="month"
                    .value=${this._month}
                    @change=${(e: Event) => {
                        const value = (e.target as HTMLInputElement).value;
                        if (value) {
                            this._month = value;
                        }
                    }}
                />
            </div>
            <div class="grid">
                ${VIEWS.map(
                    ({ title, path }) => html`
                        <section class="panel">
                            <h3>${title}</h3>
                            <pentacode-view
                                route=${`${path}?from=${from}&to=${to}`}
                                @pentacode-route-change=${(e: CustomEvent<{ route: string }>) =>
                                    this._onRouteChange(path, e)}
                            ></pentacode-view>
                        </section>
                    `
                )}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-views-demo": ViewsDemo;
    }
}
